import React, { useState } from "react";
import { GitBranch, ChevronRight, AlertCircle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

interface Consequence {
  id: string;
  choice: string;
  immediate_outcome: string;
  turn: number;
  delayed_effects?: Array<{ description: string; surfaced_turn: number }>;
  severity?: string; // "minor" | "moderate" | "major"
  is_pending?: boolean;
}

interface DecisionJournalProps {
  _consequence_history?: Consequence[];
  _pending_consequences?: Array<{ description: string; trigger?: string }>;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  minor: "bg-muted text-muted-foreground",
  moderate: "bg-amber-500/15 text-amber-600",
  major: "bg-red-500/15 text-red-600",
};

export function DecisionJournal({
  _consequence_history = [],
  _pending_consequences = [],
}: DecisionJournalProps) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState<string | null>(null);

  if (_consequence_history.length === 0 && _pending_consequences.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <GitBranch className="h-8 w-8 text-muted-foreground/50" />
        <div>
          <p className="text-sm text-muted-foreground">{t.play_decision_journal}</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-[200px]">
            {t.play_choices_pending}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <GitBranch className="h-4 w-4 text-primary" />
        <span>{t.play_decision_journal}</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">{_consequence_history.length}</Badge>
      </div>

      {_pending_consequences.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 space-y-1">
          <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
            <Clock className="h-3 w-3" />
            <span>{_pending_consequences.length} {t.play_pending_consequences}…</span>
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            {t.play_choices_pending}
          </p>
        </div>
      )}

      <ScrollArea className="max-h-[400px]">
        <div className="space-y-1.5">
          {[..._consequence_history].reverse().map((c) => (
            <button
              key={c.id}
              onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              className={cn(
                "w-full text-left rounded-md border border-border p-2 transition-colors hover:bg-accent/50",
                expanded === c.id && "bg-accent/30"
              )}
            >
              <div className="flex items-center gap-1.5">
                <ChevronRight className={cn("h-3 w-3 text-muted-foreground transition-transform", expanded === c.id && "rotate-90")} />
                <span className="text-xs font-medium text-foreground flex-1 truncate">{c.choice}</span>
                {c.severity && (
                  <Badge className={cn("text-[9px] px-1 py-0 border-0", SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.moderate)}>
                    {c.severity}
                  </Badge>
                )}
              </div>

              {expanded === c.id && (
                <div className="mt-2 ml-4 space-y-2">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_immediate}</span>
                    <p className="text-xs text-muted-foreground">{c.immediate_outcome}</p>
                  </div>

                  {c.delayed_effects && c.delayed_effects.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.play_ripple_effects}</span>
                      {c.delayed_effects.map((d, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                          <span>{d.description} <span className="text-[9px] text-muted-foreground/50">(turn {d.surfaced_turn})</span></span>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-[9px] text-muted-foreground/50">Turn {c.turn}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}